import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Col, FormControl, Row } from 'react-bootstrap';
import OnboardingHeader from './OnboardingHeader';
import OnboardingPlansPanel from '../../components/Onboarding/OnboardingPlansPanel';
import landingHeroBg from '../../assets/images/index-bg-3.png';
import estudioRelieveLogo from '../../assets/images/estudio-relieve.jpeg';
import cottonPrintLogo from '../../assets/images/cotton_print_chile_logo.jpeg';
import resilityLogo from '../../assets/images/resility-logo.png';
import ganaderaBrangusLogo from '../../assets/images/ganadera-brangus-spa.webp';
import OnboardingPlanFeaturesTable from '../../components/Onboarding/OnboardingPlanFeaturesTable';
import OnboardingStepper from '../../components/Onboarding/OnboardingStepper';
import PfInlineError from '../../components/Onboarding/PfInlineError';
import { updateOnboardingRegistrationRequest } from '../../requests/onboardingRegistration';
import {
  updateOnboardingPaymentContext,
  loadOnboardingPaymentContext,
  formatCLP,
  INTRO_PRICE_NET,
  INTRO_PRICE_GROSS
} from '../../data/platformPlans';
import { setAccountName } from '../../actions/account';

const OnboardingPlans = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const account = useSelector(state => state.account);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [contactEmail, setContactEmail] = useState(account.email || '');

  const searchParams = new URLSearchParams(location.search);
  const isIframeMode = searchParams.get('format') === 'iframe';
  const landingHeroStyle = !isIframeMode ? { '--landing-hero-bg-url': `url(${landingHeroBg})` } : {};

  const paymentContext = loadOnboardingPaymentContext();
  const registrationId = paymentContext?.registrationId;

  useEffect(() => {
    if (!registrationId) {
      history.replace({
        pathname: '/onboarding',
        search: isIframeMode ? '?format=iframe' : ''
      });
    }
    // eslint-disable-next-line
  }, []);

  // Selecting a plan only records it on the server-side registration and moves
  // to the personal-data step - the card inscription is the LAST step (D2).
  const handleSelectPlan = plan => {
    const normalizedEmail = contactEmail.trim();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setEmailError(true);
      return;
    }

    setIsLoading(true);
    setSelectedPlanId(plan.id);
    setEmailError(false);
    setErrorMessage('');

    updateOnboardingRegistrationRequest(registrationId, {
      dispatch,
      params: { registration: { platform_plan: plan.id, email: normalizedEmail } },
      successCallback: () => {
        dispatch(setAccountName({ ...account, email: normalizedEmail }));
        updateOnboardingPaymentContext({ platformPlan: plan.id, email: normalizedEmail });
        history.push({
          pathname: '/account_register',
          search: isIframeMode ? '?format=iframe' : '',
          state: { withPaymentFlow: true }
        });
      },
      failureCallback: () => {
        setIsLoading(false);
        setSelectedPlanId(null);
        setErrorMessage('No fue posible guardar el plan. Intenta nuevamente.');
      }
    });
  };

  const introCard = (
    <div className="onboarding-card-stack d-flex flex-column mt-2">
      {!isIframeMode && (
        <div className="onboarding-card-stack__logo mb-1">
          <OnboardingHeader isIframeMode={isIframeMode} />
        </div>
      )}

      {!isIframeMode && (
        <div className="d-md-none onboarding-mobile-hero mb-3">
          <h2 className="users-title font-weight-bold mb-1 text-center">Haz que tu negocio no dependa de ti</h2>
          <p className="m-0 text-center onboarding-mobile-hero__subtitle">
            Conecta SII, bancos y previred para empezar a delegar las finanzas de tu negocio
          </p>
        </div>
      )}

      <div className={isIframeMode ? 'iframe-form' : 'onboarding-card'}>
        {!isIframeMode && (
          <div className="d-md-none onboarding-card__intro text-center">
            <h3 className="font-weight-bold mb-1">Elige tu plan</h3>
          </div>
        )}

        {!isIframeMode && (
          <div className="d-none d-md-block onboarding-card__intro">
            <OnboardingStepper current={2} />
            <h1 className="pf-card__title mb-1">Elige tu plan</h1>
            <p className="pf-card__subtitle m-0">
              Registra tu tarjeta y activa la cuenta con el plan que mejor se adapte a tu negocio.
            </p>
          </div>
        )}

        <div className="onboarding-card__form mt-3">
          <div className="onboarding-card__field mb-3 onboarding-intro-price">
            <p className="onboarding-card__label font-weight-bold mb-1">
              Tu primer mes cuesta {formatCLP(INTRO_PRICE_NET)} + IVA
            </p>
            <p className="mb-0 small text-muted">
              Hoy pagarás {formatCLP(INTRO_PRICE_GROSS)} (primer mes de {formatCLP(INTRO_PRICE_NET)} neto + IVA). Desde
              el segundo mes se cobrará el valor del plan que elijas, IVA incluido.
            </p>
          </div>

          <div className={`pf-field-hero ${emailError ? 'pf-field-hero--error' : ''} mb-3`}>
            <div className="pf-field-hero__head">
              <span className="pf-label">Correo para el pago</span>
              <span className="pf-field-hero__badge">Requerido</span>
            </div>
            <FormControl
              className={`pf-field ${emailError ? 'pf-field--error' : ''}`}
              type="email"
              placeholder="tu@empresa.com"
              value={contactEmail}
              onChange={e => {
                setContactEmail(e.target.value);
                setEmailError(false);
              }}
              disabled={isLoading}
            />
            {emailError ? (
              <PfInlineError>Ingresa tu correo para continuar y activar el plan que elijas.</PfInlineError>
            ) : (
              <span className="pf-help">Te enviaremos el comprobante a este correo.</span>
            )}
          </div>

          <div className="pf-trust-note mb-3">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2743b1"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="pf-trust-note__icon"
            >
              <path d="M12 2l7 4v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <span>
              Pago 100% seguro procesado por <strong>Transbank</strong>.
            </span>
          </div>

          <div className="onboarding-card__form-actions onboarding-card__form-actions--hero">
            <Button
              className="pf-btn pf-btn--ghost"
              onClick={() =>
                history.push({
                  pathname: '/onboarding',
                  search: isIframeMode ? '?format=iframe' : ''
                })
              }
              disabled={isLoading}
            >
              ← Volver
            </Button>
          </div>
        </div>

        {!isIframeMode && (
          <>
            <div className="pf-divider" />
            <div className="onboarding-left__trusted text-center">
              <p className="pf-trust__title mb-2">+500 empresas ya confían en Parrotfy</p>
              <div className="onboarding-left__logos d-flex justify-content-center align-items-center flex-wrap mb-2">
                <img src={estudioRelieveLogo} alt="Estudio Relieve" />
                <img src={cottonPrintLogo} alt="Cotton Print Chile" />
                <img src={resilityLogo} alt="Resility" />
                <img src={ganaderaBrangusLogo} alt="Ganadera Brangus" />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="d-md-none onboarding-mobile-testimonials mt-4">
        <div className="onboarding-card onboarding-card--plans-panel mb-3">
          <OnboardingPlansPanel
            onSelectPlan={handleSelectPlan}
            isLoading={isLoading}
            selectedPlanId={selectedPlanId}
            errorMessage={errorMessage}
          />
        </div>
        <div className="onboarding-card mb-3">
          <OnboardingPlanFeaturesTable />
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={isIframeMode ? 'onboarding-page onboarding-page--iframe' : 'onboarding-page landing-hero'}
      style={!isIframeMode ? landingHeroStyle : undefined}
    >
      {!isIframeMode && (
        <>
          <div className="landing-hero__bg" />
          <div className="landing-hero__wash" />
          <div className="landing-hero__darken" />
          <div className="landing-hero__tint" />
        </>
      )}
      <div
        className={
          isIframeMode
            ? 'onboarding-page__content d-flex align-items-center justify-content-between'
            : 'landing-hero__content d-flex align-items-center justify-content-between'
        }
      >
        {isIframeMode ? (
          <div className="iframe-column w-100">
            <div className="onboarding-card onboarding-card--plans-panel mb-3">
              <OnboardingPlansPanel
                onSelectPlan={handleSelectPlan}
                isLoading={isLoading}
                selectedPlanId={selectedPlanId}
                errorMessage={errorMessage}
              />
            </div>
            <div className="onboarding-card mb-3">
              <OnboardingPlanFeaturesTable />
            </div>
          </div>
        ) : (
          <div className="w-100">
            <Row className="justify-content-center align-items-start" noGutters>
              <Col xs={12} md={4} className="pl-md-2 pr-2 pl-2 col-md-4 col-12">
                {introCard}
              </Col>
              <Col
                xs={12}
                md={8}
                className="onboarding-right-col d-none d-md-flex align-items-stretch justify-content-center"
              >
                <div className="onboarding-right onboarding-right--plans d-flex flex-column w-100">
                  <div className="onboarding-right__intro onboarding-right__intro--compact text-center">
                    <h2 className="users-title font-weight-bold mb-0">Un plan para cada etapa</h2>
                  </div>

                  <div className="onboarding-card onboarding-card--plans-panel mt-2">
                    <OnboardingPlansPanel
                      onSelectPlan={handleSelectPlan}
                      isLoading={isLoading}
                      selectedPlanId={selectedPlanId}
                      errorMessage={errorMessage}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Row noGutters>
              <Col xs={12} className="px-2 d-none d-md-block">
                <div className="onboarding-card mt-4 mb-5">
                  <OnboardingPlanFeaturesTable />
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPlans;
